import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";
import ErrorPopupComponent from "../error-popup/error-popup.component.vue";
import PreviewPopupComponent from "../preview-popup/preview-popup.component.vue";
import { emailCampaignApi } from "@/providers/apis/email-campaign";
import { notAcceptedEmailApi } from "@/providers/apis/not-accepted-email-form";
import { getApplicationDetails } from "@/providers/apis/getApplictionDetails";
import { commonService } from "@/providers/services/common-service";
import { appConfigService } from "@/providers/services/app/app-config";
import { utils } from "@/utils/utils";

@Component({
  name: "reviewer-email-page",
  components: {
    AppLoaderComponent,
    ErrorPopupComponent,
    PreviewPopupComponent,
  },
})
export default class ReviewerEmailPage extends Vue {
  public campaignData: any[] = [];
  public selectedCampaign: any = null;

  public limit = 10;
  public offset = 0;
  public currentPage = 1;
  public page = 1;
  public totalItem = 0;

  public isLoading = true;
  public isLoadingMore = false;
  public isDataEmpty = false;
  public errorMessage = "";

  public activeTab = "all";
  public searchText = "";

  // ---- New Email composer ----
  // mode: INDIVIDUAL (one peer, personal email) | GROUP | ALL (campaigns).
  public isSaving = false;
  public submitError = "";
  public mode = "GROUP";

  // Individual
  public peerSearch = "";
  public peerResults: any[] = [];
  public showPeerResults = false;
  public selectedPeer: any = null;

  // Group
  public groupOptions: any[] = [];
  public selectedGroups: string[] = [];
  public showGroupDropdown = false;
  public groupSearch = "";

  // Shared
  public subjectInput = "";
  public bodyInput = "";
  public ccChips: string[] = [];
  public bccChips: string[] = [];
  public files: File[] = [];
  public fileError = "";

  // In-app preview of a campaign attachment.
  public previewFileData: any[] = [];
  public showPreviewPopup = false;

  public tableFields = [
    { key: "subject", label: "Subject", sortable: false },
    { key: "audience", label: "Audience", sortable: false },
    {
      key: "status",
      label: "Status",
      sortable: false,
      thClass: "application_status",
      tdClass: "progress-status",
    },
    { key: "createdAt", label: "Created", sortable: false },
    {
      key: "action",
      label: "",
      sortable: false,
      thClass: "application_action pointer-events",
      tdClass: "actions",
    },
  ];

  public modeOptions = [
    { text: "Group", value: "GROUP" },
    { text: "All CPRS", value: "ALL" },
  ];

  public created() {
    this.getGroups();
    this.getCampaigns();
  }

  public get isIndividual(): boolean {
    return this.mode === "INDIVIDUAL";
  }

  public get isGroup(): boolean {
    return this.mode === "GROUP";
  }

  /** Selected group names, for the dropdown's closed label. */
  public get selectedGroupsLabel(): string {
    return this.groupOptions
      .filter((opt: any) => this.selectedGroups.includes(opt.value))
      .map((opt: any) => opt.text)
      .join(", ");
  }

  /** Group options filtered by the in-dropdown search box. */
  public get filteredGroupOptions(): any[] {
    const q = this.groupSearch.trim().toLowerCase();
    if (!q) {
      return this.groupOptions;
    }
    return this.groupOptions.filter((opt: any) =>
      String(opt.text).toLowerCase().includes(q)
    );
  }

  /** Selected group option objects, rendered as removable chips. */
  public get selectedGroupChips(): any[] {
    return this.groupOptions.filter((opt: any) =>
      this.selectedGroups.includes(opt.value)
    );
  }

  /** Remove a group from the selection (chip close button). */
  public removeGroup(value: string) {
    const idx = this.selectedGroups.indexOf(value);
    if (idx !== -1) {
      this.selectedGroups.splice(idx, 1);
    }
  }

  public getGroups() {
    emailCampaignApi
      .getGroups()
      .then((data: any) => {
        const groups = (data && data.groups) || [];
        this.groupOptions = groups.map((group: any) => ({
          // Display the enum as readable text (PART_ONE_RECEIVED ->
          // "Part One Received"); the raw value still goes to the API.
          text: this.formatGroupName(group.name),
          value: group.name,
          // Recipient count, when the API supplies one (shown on the chip).
          count:
            group.count ??
            group.total ??
            group.recipientCount ??
            group.userCount ??
            null,
        }));
      })
      .catch(() => {
        this.groupOptions = [];
      });
  }

  /** Turns an UPPER_SNAKE group name into Title Case with spaces. */
  public formatGroupName(name: string): string {
    return String(name || "")
      .split("_")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  public getCampaigns(append = false) {
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
    }
    const filters: any = {
      limit: this.limit,
      offset: this.offset,
    };
    if (this.activeTab === "sent") {
      // Backend marks a finished send as COMPLETED (there is no SENT status),
      // so the Sent tab lists completed campaigns.
      filters.status = "COMPLETED";
    }
    if (this.searchText) {
      filters.searchText = encodeURIComponent(this.searchText);
    }
    emailCampaignApi
      .getCampaigns(filters)
      .then((data: any) => {
        const emails = (data && data.emails) || [];
        this.totalItem = (data && data.totalCount) || 0;
        if (append) {
          this.campaignData = this.campaignData.concat(emails);
        } else {
          this.campaignData = emails;
          // Open the newest thread straight away rather than leaving the
          // reader on "Select a conversation"; a fresh page always has one.
          // isConversationOpen stays false, so mobile still lands on the list
          // and only opens the detail when a row is tapped.
          this.selectedCampaign = this.campaignData.length
            ? this.campaignData[0]
            : null;
          this.isConversationOpen = false;
        }
        this.isDataEmpty = !this.campaignData.length;
        this.isLoading = false;
        this.isLoadingMore = false;
      })
      .catch((error: any) => {
        this.isDataEmpty = !this.campaignData.length;
        this.isLoading = false;
        this.isLoadingMore = false;
        this.errorMessage = utils.errorMessage(error);
      });
  }

  /** Infinite scroll: fetch the next page when the list nears the bottom. */
  public onListScroll(event: any) {
    if (this.isLoading || this.isLoadingMore) {
      return;
    }
    if (this.campaignData.length >= this.totalItem) {
      return;
    }
    const el = event.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      this.offset += this.limit;
      this.getCampaigns(true);
    }
  }

  /**
   * Search runs against the campaign list server-side (`searchText`, matched
   * on subject and body), so it restarts the list rather than filtering what
   * is already loaded - the match may well be on a page the infinite scroll
   * has not reached.
   */
  public onSearch() {
    this.offset = 0;
    this.currentPage = 1;
    this.campaignData = [];
    this.getCampaigns();
  }

  /** Clearing the box is a search for nothing, i.e. the unfiltered list. */
  public onClearSearch() {
    if (!this.searchText) {
      return;
    }
    this.searchText = "";
    this.onSearch();
  }

  public switchTab(tab: string) {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.offset = 0;
    this.currentPage = 1;
    this.campaignData = [];
    this.getCampaigns();
  }

  public loadContent(page: number) {
    this.page = page;
    this.offset = (this.page - 1) * this.limit;
    this.getCampaigns();
  }

  /**
   * On mobile the two panes are a master-detail: the list is the page, and
   * opening a row slides the conversation over it. Desktop shows both panes
   * side by side and ignores this flag entirely.
   */
  public isConversationOpen = false;

  public selectCampaign(row: any) {
    this.selectedCampaign = row.item;
    this.isConversationOpen = true;
  }

  public backToList() {
    this.isConversationOpen = false;
  }

  /**
   * The paperclip on a list row means exactly one thing: this campaign carries
   * files. `attachments` is a jsonb column, so it arrives as an array when
   * files were attached and as null/absent otherwise.
   */
  public hasAttachments(item: any): boolean {
    const attachments = item && item.attachments;
    if (Array.isArray(attachments)) {
      return attachments.length > 0;
    }
    return false;
  }

  /** The campaign's attachment list (empty array when there are none). */
  public campaignAttachments(item: any): any[] {
    return item && Array.isArray(item.attachments) ? item.attachments : [];
  }

  /** Public URL of a stored attachment so it can be opened/viewed. */
  public attachmentUrl(att: any): string {
    return `${appConfigService.getS3FilesPathUrl()}/${att.uploadedFileName}`;
  }

  /** Open an attachment in the in-app preview popup (instead of a new tab). */
  public openAttachmentPreview(att: any) {
    this.previewFileData = [att];
    this.showPreviewPopup = true;
  }

  public hidePreviewPopup(event: boolean) {
    if (event) {
      this.showPreviewPopup = false;
      this.previewFileData = [];
    }
  }

  /** File extension for the badge (PDF, PNG…). */
  public attExtLabel(att: any): string {
    const name = att.originalFileName || att.uploadedFileName || "";
    const ext = String(name).split(".").pop();
    return (ext || "FILE").toUpperCase();
  }

  /** Colour class for the attachment badge. */
  public attExtClass(att: any): string {
    const ext = this.attExtLabel(att).toLowerCase();
    if (ext === "pdf") return "ext-pdf";
    if (ext === "png" || ext === "jpg" || ext === "jpeg") return "ext-img";
    return "ext-doc";
  }

  /** A short plain-text preview of the message (bodyHtml stripped). */
  public bodyPreview(item: any): string {
    const html = item && item.bodyHtml ? String(item.bodyHtml) : "";
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /** The signed-in staff member is the sender of a campaign. */
  public get senderName(): string {
    const user = utils.getUserDetails() || ({} as any);
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || "You";
  }

  public get senderInitials(): string {
    return this.senderName
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }

  public audienceLabel(row: any): string {
    if (row.audienceType === "GROUP") {
      const groups = row.audienceCriteria && row.audienceCriteria.groups;
      return Array.isArray(groups) && groups.length
        ? groups.map((g: string) => this.formatGroupName(g)).join(", ")
        : "Groups";
    }
    return "All CPRS";
  }

  /**
   * Compact audience for the list card: first group only, so a long group set
   * doesn't fill the card. The "+N more" count is rendered separately (see
   * audienceMoreCount) so it can be styled in the brand colour.
   */
  public audienceFirst(row: any): string {
    if (row.audienceType !== "GROUP") {
      return "All CPRS";
    }
    const groups = (row.audienceCriteria && row.audienceCriteria.groups) || [];
    if (!groups.length) {
      return "Groups";
    }
    return this.formatGroupName(groups[0]);
  }

  /** How many groups beyond the first, for the "+N more" badge. */
  public audienceMoreCount(row: any): number {
    if (row.audienceType !== "GROUP") {
      return 0;
    }
    const groups = (row.audienceCriteria && row.audienceCriteria.groups) || [];
    return groups.length > 1 ? groups.length - 1 : 0;
  }

  public statusLabel(status: string): string {
    if (!status) {
      return "Pending";
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  public statusClass(status: string): string {
    const value = (status || "").toUpperCase();
    if (value === "SENT" || value === "COMPLETED") return "closed";
    if (value === "FAILED") return "not-accepted";
    return "received";
  }

  public formatDate(value: string): string {
    return value ? moment(value).format("MM-DD-YYYY") : "—";
  }

  // ---- Composer ----
  public openCompose() {
    this.resetComposer();
    this.$bvModal.show("email-compose-modal");
  }

  public closeCompose() {
    this.$bvModal.hide("email-compose-modal");
  }

  public setMode(mode: string) {
    this.mode = mode;
    this.submitError = "";
  }

  private resetComposer() {
    this.mode = "GROUP";
    this.showGroupDropdown = false;
    this.groupSearch = "";
    this.peerSearch = "";
    this.peerResults = [];
    this.showPeerResults = false;
    this.selectedPeer = null;
    this.selectedGroups = [];
    this.subjectInput = "";
    this.bodyInput = "";
    this.ccChips = [];
    this.bccChips = [];
    this.files = [];
    this.submitError = "";
    this.$validator.reset();
  }

  // ---- Individual: peer search ----
  public searchPeers() {
    this.selectedPeer = null;
    const query = this.peerSearch.trim();
    if (query.length < 2) {
      this.peerResults = [];
      this.showPeerResults = false;
      return;
    }
    getApplicationDetails
      .getPeersApplicationList({
        email: encodeURIComponent(query),
        limit: 10,
        offset: 0,
      })
      .then((data: any) => {
        this.peerResults = (data && data.peers) || [];
        this.showPeerResults = true;
      })
      .catch(() => {
        this.peerResults = [];
        this.showPeerResults = false;
      });
  }

  public selectPeer(peer: any) {
    this.selectedPeer = peer;
    this.peerSearch = peer.email;
    this.showPeerResults = false;
  }

  public peerLabel(peer: any): string {
    const name = `${peer.firstName || ""} ${peer.lastName || ""}`.trim();
    return name ? `${name} (${peer.email})` : peer.email;
  }

  // ---- Attachment (Individual only) ----
  public onFilesSelected(event: any) {
    this.fileError = "";
    const chosen: File[] = Array.from(event?.target?.files || []);
    const allowed = /\.(jpe?g|png|pdf)$/i;
    const accepted: File[] = [];
    for (const file of chosen) {
      if (allowed.test(file.name)) {
        accepted.push(file);
      } else {
        this.fileError = "Please upload files in .jpg, .png, or .pdf format.";
      }
    }
    this.files = this.files.concat(accepted);
    event.target.value = "";
  }

  /** File extension in caps for the badge (PDF, PNG, JPG…). */
  public fileExtLabel(name: string): string {
    const ext = String(name || "")
      .split(".")
      .pop();
    return (ext || "FILE").toUpperCase();
  }

  /** Colour class for the badge, by file type. */
  public fileExtClass(name: string): string {
    const ext = this.fileExtLabel(name).toLowerCase();
    if (ext === "pdf") return "ext-pdf";
    if (ext === "png" || ext === "jpg" || ext === "jpeg") return "ext-img";
    return "ext-doc";
  }

  /** Human-readable file size (34 KB, 1.2 MB). */
  public formatFileSize(bytes: number): string {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  public removeFile(index: number) {
    this.files.splice(index, 1);
  }

  // Email sending is enabled. NOTE: on beta this triggers a real send to every
  // recipient via the live scheduler; an "All CPRS" or large-group send reaches
  // real inboxes. Set back to true to disable the send path.
  public sendingDisabled = false;

  public async submit() {
    this.submitError = "";
    if (this.sendingDisabled) {
      this.submitError =
        "Email sending is currently disabled. No email was sent.";
      return;
    }
    const valid = await this.$validator.validateAll();
    if (!valid) {
      return;
    }

    if (this.isIndividual) {
      return this.submitIndividual();
    }
    if (this.isGroup && !this.selectedGroups.length) {
      this.submitError = "Select at least one group.";
      return;
    }
    return this.submitCampaign();
  }

  private submitIndividual() {
    if (!this.selectedPeer) {
      this.submitError = "Search and select a peer to email.";
      return;
    }
    this.isSaving = true;
    const formData = new FormData();
    formData.append(
      "applicationId",
      this.selectedPeer.id || this.selectedPeer.applicationId || ""
    );
    formData.append("email", this.selectedPeer.email);
    formData.append("ccAddress", this.ccChips.join(","));
    formData.append("subject", this.subjectInput.trim());
    formData.append("message", this.bodyInput.trim());
    for (const file of this.files) {
      formData.append("files", file);
    }
    notAcceptedEmailApi
      .sendEmailData(formData)
      .then(() => {
        this.isSaving = false;
        this.closeCompose();
        this.refreshList();
      })
      .catch((error: any) => {
        this.isSaving = false;
        this.submitError = utils.errorMessage(error);
      });
  }

  private async submitCampaign() {
    this.isSaving = true;

    // Campaigns are JSON, so files go up through the shared upload endpoint
    // first and only their names travel in the payload. The API stores them on
    // the campaign's attachments column for the send path to pick up.
    let attachments: any[] = [];
    if (this.files.length) {
      try {
        const result: any = await commonService.uploadFile(this.files);
        attachments = (result.data || []).map((file: any) => ({
          originalFileName: file.originalFileName || file.name,
          uploadedFileName: file.fileName,
        }));
      } catch (error: any) {
        this.isSaving = false;
        this.submitError = utils.errorMessage(error);
        return;
      }
    }

    const payload: any = {
      audienceType: this.mode, // GROUP or ALL
      subject: this.subjectInput.trim(),
      bodyHtml: `<p>${this.bodyInput.trim().replace(/\n/g, "<br>")}</p>`,
    };
    if (this.isGroup) {
      payload.groups = this.selectedGroups;
    }
    if (this.ccChips.length) {
      payload.cc = this.ccChips;
    }
    if (this.bccChips.length) {
      payload.bcc = this.bccChips;
    }
    if (attachments.length) {
      payload.attachments = attachments;
    }
    emailCampaignApi
      .createCampaign(payload)
      .then(() => {
        this.isSaving = false;
        this.closeCompose();
        this.refreshList();
      })
      .catch((error: any) => {
        this.isSaving = false;
        this.submitError = utils.errorMessage(error);
      });
  }

  private refreshList() {
    this.offset = 0;
    this.currentPage = 1;
    this.activeTab = "all";
    this.getCampaigns();
  }

  public closdedErrorPopup(event: boolean) {
    if (event) {
      this.errorMessage = "";
    }
  }
}
